import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
  Form,
  FormField,
  Button,
  MobileNavBar,
  useZodForm,
  useModalControls,
} from '@noor/ui';
import { getApiErrorMessage } from '@noor/api';
import { toast, useFormSeed, type ModalRenderProps } from '@noor/lib';
import { cn } from '@noor/lib/cn';
import { useRestaurantCategories } from '@noor/eats-api';
import { useBranchProducts } from '@/entities/menu';
import { useCuisines } from '../model/use-cuisines';
import {
  buildBranchFormSchema,
  type BranchFormValues,
  type BranchProduct,
} from '../model/schema';
import { useBranchDetails } from '../model/use-branch-details';
import { useUpsertBranch } from '../model/use-upsert-branch';
import { useSyncBranchProducts } from '../model/use-sync-branch-products';
import { emptyDefaults, missingCreateBranchFields, toDefaults } from '../lib/branch-form';
import { toBranchProduct } from '../lib/branch-products';
import { AddressFieldMobile } from './address-field.mobile';
import { PhotoFieldMobile } from './photo-field.mobile';
import { ScheduleField } from './schedule-field';
import { WizardSteps } from './wizard-steps';
import { ProductStep } from './product-step';

/** Filled grey pill field — the mobile design's one input treatment. */
const FIELD = 'h-12 rounded-3xl border-none bg-muted px-4 text-sm shadow-none';
const MULTI_SELECT_FIELD =
  'min-h-12 rounded-3xl border-none bg-muted px-4 py-2 text-sm shadow-none';

/** Figma «Basic» textarea (26614:20485): a WHITE bordered box, 92px tall. */
const TEXTAREA =
  'min-h-[92px] resize-none rounded-3xl border-input bg-card px-4 py-2.5 text-sm shadow-xs focus-visible:border-input-border-focus';

/** Label→control gap: 8px for the pills, 6px for the description boxes. */
const FIELD_GROUP = 'gap-2';
const DESCRIPTION_GROUP = 'gap-1.5';

/** Validation text that takes up no room until there is something to say. */
const MESSAGE = 'min-h-0 px-1 empty:hidden';

/** Figma label: 14px medium, gray-700, 4px inset. */
const LABEL =
  'flex items-center gap-px px-1 text-sm font-medium tracking-tight text-secondary-foreground';

/** The wizard's two steps, in order — the same pair the desktop dialog runs. */
const STEPS = ['details', 'products'] as const;

/** The three platform languages, in the order the design lists them. */
const LOCALES = [
  { suffix: 'Ru', tag: '(Ru)' },
  { suffix: 'Uz', tag: '(Uz)' },
  { suffix: 'En', tag: '(En)' },
] as const;

type BranchSheetMobileProps =
  | ModalRenderProps<'add-branch'>
  | ModalRenderProps<'edit-branch'>;

/**
 * Add / edit branch on mobile (Figma node 26614:20465) — a full-screen
 * native-app takeover, not the desktop's centered 880px dialog rendered
 * narrow.
 *
 * Rendered through the bare `Dialog` primitives rather than the shared `Modal`
 * shell: that shell centers a card with a close-X over a backdrop, while this
 * screen IS the surface — full-bleed, its own back arrow, no close-X — the
 * same treatment `product-modal.mobile.tsx` uses.
 *
 * The layout is a single column throughout, where the desktop dialog pairs
 * fields two-per-row and rules the sections off with hairline dividers. Only
 * ONE divider survives here, under the title (Figma 26614:20469), and the
 * design reorders the fields: the two photo tiles move UP to sit directly
 * under the address, ahead of the menu source and the free-delivery threshold.
 *
 * Everything below the view is shared with desktop — the same schema, the same
 * `useBranchDetails()` seed, the same `useUpsertBranch()` write, including its
 * moderation-gated and photo-failure reporting. A mobile variant is a view,
 * never a second data path.
 */
export function BranchSheetMobile({ payload, close }: BranchSheetMobileProps) {
  const { t, i18n } = useTranslation('eats');
  const { open } = useModalControls();
  const editingId = payload.mode === 'edit' ? payload.branchId : undefined;
  const isEditing = editingId !== undefined;
  const upsert = useUpsertBranch();
  const syncProducts = useSyncBranchProducts();

  // The wizard's two steps, shared with the desktop dialog — see
  // `branch-modal.desktop.tsx` for why step 1 is what creates the branch.
  const [step, setStep] = useState(0);
  const [createdId, setCreatedId] = useState<string | undefined>(undefined);
  const branchId = editingId ?? createdId;

  const [productRows, setProductRows] = useState<BranchProduct[]>([]);
  const [seededProducts, setSeededProducts] = useState<BranchProduct[]>([]);

  // Seeds the editor with every translation plus the stored schedule in one
  // call. Idle for a create.
  const { data, dataUpdatedAt } = useBranchDetails(editingId);

  // What the branch already stocks — step 2's starting rows on an edit.
  const { data: branchProducts, dataUpdatedAt: productsUpdatedAt } =
    useBranchProducts(branchId);
  const { data: restaurantCategories } = useRestaurantCategories();
  const { data: cuisines = [] } = useCuisines();
  const cuisineOptions = useMemo(
    () => cuisines.map(({ id, name }) => ({ value: id, label: name })),
    [cuisines],
  );
  const categoryOptions = useMemo(
    () =>
      (restaurantCategories?.list ?? []).map((category) => ({
        label: category.name,
        value: category.id,
        disabled: !category.is_active,
      })),
    [restaurantCategories],
  );

  useFormSeed(true, branchId, productsUpdatedAt, () => {
    const rows = (branchProducts ?? []).map(toBranchProduct);
    setProductRows(rows);
    setSeededProducts(rows);
  });

  const schema = useMemo(() => buildBranchFormSchema(t, !isEditing), [t, isEditing]);
  const form = useZodForm(schema, { defaultValues: emptyDefaults(), mode: 'onSubmit' });

  // Re-seed when the by-id read delivers a NEWER record — that is what fills in
  // the translations, photos and schedule. Keyed on `dataUpdatedAt`, which only
  // advances when a fetch resolves, so it cannot reset the form mid-typing.
  const { reset } = form;
  // Keyed on `editingId`, NOT the wizard's `branchId`: a create mints an id on
  // leaving step 1, and keying on that would re-seed the form from
  // `emptyDefaults()` and wipe everything just typed.
  useFormSeed(true, editingId, dataUpdatedAt, () => {
    reset(
      data
        ? toDefaults(
            data.restaurant,
            data.schedule,
            i18n.language.split('-')[0] as 'ru' | 'uz' | 'en',
          )
        : emptyDefaults(),
    );
  });

  /** Save the branch record and advance — see the desktop dialog's note. */
  function handleDetailsSubmit(values: BranchFormValues): void {
    upsert.mutate(
      {
        values,
        branchId,
        // What the photo slots held when the sheet opened — the only way to
        // tell an untouched empty slot from one the merchant just cleared.
        stored: {
          icon: Boolean(data?.restaurant.icon),
          background: Boolean(data?.restaurant.background_image),
        },
      },
      {
        onSuccess: ({ status, branchId: savedId, photosFailed, photoError }) => {
          // A gated merchant's write is QUEUED for review rather than applied,
          // so the list won't show it yet — said plainly instead of closing
          // silently, which would read as "saved".
          if (status === 'pending') {
            toast.success(t('branches.form.pendingReview'));
          } else {
            toast.success(
              isEditing ? t('branches.form.updated') : t('branches.form.created'),
            );
          }
          // The branch saved but its photos did not. Reported AFTER the save so
          // it reads as "everything else went through", not as a failed submit.
          if (photosFailed) {
            toast.error(getApiErrorMessage(photoError, t('branches.form.photoError')));
          }

          if (savedId !== undefined) setCreatedId(savedId);

          // A MODERATED create has no branch to attach products to — the write
          // is a receipt, not a record — so the wizard ends here rather than
          // advancing to a step that could only fail.
          if (!isEditing && savedId === undefined) {
            close();
            return;
          }
          setStep(1);
        },
        onError: (error) =>
          toast.error(getApiErrorMessage(error, t('branches.form.error'))),
      },
    );
  }

  /** Write step 2's product list against the saved branch, then close. */
  function handleProductsSubmit(): void {
    if (branchId === undefined) return;

    syncProducts.mutate(
      { branchId, rows: productRows, seeded: seededProducts },
      {
        onSuccess: ({ failed, firstError }) => {
          if (failed > 0) {
            toast.error(
              getApiErrorMessage(
                firstError,
                t('branches.form.products.partialError', { count: failed }),
              ),
            );
          } else {
            toast.success(t('branches.form.products.saved'));
          }
          close();
        },
        onError: (error) =>
          toast.error(getApiErrorMessage(error, t('branches.form.products.error'))),
      },
    );
  }

  const title = t(isEditing ? 'common:modal.edition' : 'common:modal.creation');
  const isBusy = upsert.isPending || syncProducts.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-50 flex flex-col overflow-hidden bg-card',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
          // Without this Radix focuses the first field on open, which raises
          // the keyboard over a form the merchant hasn't read yet.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Visually hidden — the screen states its own title below; Radix
           * still requires a `DialogTitle` for a11y. */}
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>

          <MobileNavBar
            className="shrink-0"
            title={t('nav.tabs.venues')}
            leading={
              <MobileNavBar.Back onClick={close} aria-label={t('common:actions.back')} />
            }
          />

          {/* `min-h-0` on the form and its scroll region both: a flex child's
           * default `min-height:auto` refuses to shrink below its content,
           * which silently defeats every ancestor's overflow. */}
          <Form
            form={form}
            onSubmit={handleDetailsSubmit}
            onInvalid={(errors) => {
              const fields = isEditing ? [] : missingCreateBranchFields(errors);
              toast.error(
                fields.length > 0
                  ? t('branches.form.requiredFieldsSummary', {
                      fields: fields.join(', '),
                    })
                  : t('branches.form.validationSummary'),
              );
            }}
            className="flex min-h-0 flex-1 flex-col gap-0"
          >
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 pb-28">
              {/* The screen's own title over the design's single hairline
               * (Figma 26614:20466 / 26614:20469). */}
              <div className="flex flex-col gap-4">
                <p className="px-0 text-[1.375rem] leading-none font-semibold tracking-tight text-foreground">
                  {title}
                </p>
                <div className="h-px shrink-0 rounded-sm bg-border" />
              </div>

              {/* The same two-step indicator the desktop dialog uses — the
               * flow is identical, only the surface differs. */}
              <WizardSteps
                steps={STEPS.map((id) => ({
                  id,
                  label: t(`branches.form.steps.${id}`),
                }))}
                current={step}
                onSelect={setStep}
              />

              {/* Step 1 is HIDDEN rather than unmounted: the form owns its
               * field state, and unmounting would drop every typed value. */}
              <div className={step === 0 ? 'flex flex-col gap-6' : 'hidden'}>
                <FormField name="name" className={FIELD_GROUP}>
                  <FormField.Label className={LABEL}>
                    {t('branches.form.nameLabel')}
                  </FormField.Label>
                  <FormField.Input
                    className={FIELD}
                    placeholder={t('branches.form.namePlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                <AddressFieldMobile
                  label={t('branches.form.addressLabel')}
                  placeholder={t('branches.form.addressPlaceholder')}
                  pickLabel={t('branches.form.pickOnMap')}
                  fieldClassName={FIELD}
                />

                <FormField name="phone" className={FIELD_GROUP}>
                  <FormField.Label className={LABEL}>
                    {t('branches.form.phoneLabel')} <span className="text-info">*</span>
                  </FormField.Label>
                  <FormField.Phone
                    className={FIELD}
                    placeholder={t('branches.form.phonePlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                {/* The two uploaders, the icon square beside a wide background
                 * (Figma 26614:20475 / 26614:20479 — 106px and 232px of the
                 * 358px content width, 20px apart). Proportional rather than
                 * fixed px so the pair still fits a 360px phone. */}
                <div className="flex gap-5">
                  <PhotoFieldMobile
                    name="icon"
                    label={t('branches.form.iconLabel')}
                    uploadLabel={t('branches.form.uploadPhoto')}
                    hint={t('branches.form.photoHint')}
                    removeLabel={t('branches.form.removePhoto')}
                    className="w-[106px] shrink-0"
                  />
                  <PhotoFieldMobile
                    name="backgroundImage"
                    label={t('branches.form.backgroundLabel')}
                    uploadLabel={t('branches.form.uploadBackground')}
                    hint={t('branches.form.photoHint')}
                    removeLabel={t('branches.form.removePhoto')}
                    className="min-w-0 flex-1"
                  />
                </div>

                <div className="flex flex-col gap-6">
                  {(['minPrepTime', 'maxPrepTime'] as const).map((name) => (
                    <FormField key={name} name={name} className={FIELD_GROUP}>
                      <FormField.Label className={LABEL}>
                        {t(
                          `branches.form.${name === 'minPrepTime' ? 'minPrepTimeLabel' : 'maxPrepTimeLabel'}`,
                        )}{' '}
                        <span className="text-info">*</span>
                      </FormField.Label>
                      <FormField.Input type="number" min={0} step={1} className={FIELD} />
                      <FormField.Message className={MESSAGE} />
                    </FormField>
                  ))}
                </div>

                {/* «Источник меню» and «Служба доставки» are not asked here —
                 * see the desktop dialog's note. Both still travel on the
                 * write, seeded from the stored branch or the form defaults. */}

                {isEditing && data?.restaurant.is_popular && (
                  <p className="text-sm font-medium">
                    {t('branches.form.isPopularLabel')}
                  </p>
                )}
                <FormField name="cuisineIds" className={FIELD_GROUP}>
                  <FormField.Label className={LABEL}>
                    {t('branches.form.cuisinesLabel')}
                  </FormField.Label>
                  <FormField.MultiSelect
                    options={cuisineOptions}
                    className={MULTI_SELECT_FIELD}
                    placeholder={t('branches.form.cuisinesLabel')}
                  />
                </FormField>
                <FormField name="pickupAvailable" className={FIELD_GROUP}>
                  <FormField.Checkbox label={t('branches.form.pickupAvailableLabel')} />
                </FormField>
                <FormField name="restaurantCategoryIds" className={FIELD_GROUP}>
                  <FormField.Label className={LABEL}>
                    {t('branches.form.restaurantCategoryLabel')}
                  </FormField.Label>
                  <FormField.MultiSelect
                    options={categoryOptions}
                    className={MULTI_SELECT_FIELD}
                    placeholder={t('branches.form.restaurantCategoryPlaceholder')}
                    searchPlaceholder={t('branches.form.restaurantCategorySearch')}
                    emptyText={t('branches.form.restaurantCategoryEmpty')}
                    selectAllLabel={t('branches.form.restaurantCategorySelectAll')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                <FormField name="freeDeliveryThreshold" className={FIELD_GROUP}>
                  <FormField.Label className={LABEL}>
                    {t('branches.form.thresholdLabel')}
                  </FormField.Label>
                  {/* `Price`, not a bare numeric input: the field is an amount in
                    so'm, so it wants the thousands grouping and the trailing
                    «сум» unit the other money boxes have. */}
                  <FormField.Price
                    className={FIELD}
                    placeholder={t('branches.form.thresholdPlaceholder')}
                    suffix={t('branches.form.currencySuffix')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                <FormField name="minOrderAmount" className={FIELD_GROUP}>
                  <FormField.Label className={LABEL}>
                    {t('branches.form.minOrderLabel')}
                  </FormField.Label>
                  <FormField.Price
                    className={FIELD}
                    placeholder={t('branches.form.minOrderPlaceholder')}
                    suffix={t('branches.form.currencySuffix')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                {/* Description is captured per language, each row labelled with
                 * the language it holds. All three are required: a branch
                 * missing a translation renders blank in that locale on the
                 * storefront. */}
                {LOCALES.map(({ suffix, tag }) => (
                  <FormField
                    key={suffix}
                    name={`description${suffix}`}
                    className={DESCRIPTION_GROUP}
                  >
                    <FormField.Label className={LABEL}>
                      {t('branches.form.descriptionLabel')}
                      {/* The design marks a required field in BLUE here, not the
                       * brand orange — orange is the accent, and using it would
                       * read as an action rather than a requirement. */}
                      <span className="text-info">*</span>
                      <span className="font-normal text-muted-foreground">{tag}</span>
                    </FormField.Label>
                    <FormField.TextArea
                      className={TEXTAREA}
                      placeholder={t('branches.form.descriptionPlaceholder')}
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>
                ))}

                <ScheduleField label={t('branches.form.scheduleLabel')} />
              </div>

              {/* Step 2 — the product list, one column on a phone. */}
              <div className={step === 1 ? 'block' : 'hidden'}>
                <ProductStep rows={productRows} onChange={setProductRows} />
              </div>

              <Form.RootError />
            </div>

            {/* The action band pinned below the scroll area, full width
             * (Figma 26614:20661). Step 2 STACKS «Назад» under the primary
             * rather than pairing them side by side as desktop does — at
             * phone width two half-width buttons leave neither a
             * comfortable target. */}
            <div className="flex shrink-0 flex-col gap-2 px-4 pt-2 pb-6">
              {step === 0 ? (
                <Button
                  type="submit"
                  size="lg"
                  className="h-12 w-full rounded-full text-base font-semibold"
                  disabled={isBusy}
                >
                  {t('common:actions.next')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    size="lg"
                    onClick={handleProductsSubmit}
                    disabled={isBusy || branchId === undefined}
                    className="h-12 w-full rounded-full text-base font-semibold"
                  >
                    {t('common:actions.save')}
                  </Button>
                  <Button
                    type="button"
                    size="lg"
                    variant="secondary"
                    onClick={() => setStep(0)}
                    disabled={isBusy}
                    className="h-12 w-full rounded-full text-base font-semibold"
                  >
                    {t('common:actions.back')}
                  </Button>
                </>
              )}
            </div>
          </Form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
